import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import AppHeader from '../components/AppHeader';
import { formatPrice, getImageUrl } from '../utils/currency';
import { Plus, Package, ShoppingCart, CheckCircle, Clock, XCircle, X, DollarSign, Hash, Tag, Calendar, Trash2, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, checkAuth } = useAuth();
  const { t, language } = useLanguage();
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dashImgIndex, setDashImgIndex] = useState(0);

  useEffect(() => {
    checkAuth();
    fetchMyOffers();
  }, []);

  const fetchMyOffers = async () => {
    try {
      const [offersRes, requestsRes] = await Promise.all([
        axios.get(`${API}/offers?status=&page_size=100`),
        axios.get(`${API}/requests?status=&page_size=100`)
      ]);
      
      const allOffers = offersRes.data.items || offersRes.data;
      const allRequests = requestsRes.data.items || requestsRes.data;
      const myOffers = allOffers.filter(o => o.company_id === user.company_id);
      const myRequests = allRequests.filter(r => r.company_id === user.company_id);
      
      setOffers(myOffers);
      setRequests(myRequests);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return (
      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${styles[status]}`}>
        {t(status)}
      </span>
    );
  };

  const openDetail = async (id, type) => {
    setDetailLoading(true);
    try {
      const endpoint = type === 'offer' ? 'offers' : 'requests';
      const { data } = await axios.get(`${API}/${endpoint}/${id}`);
      setSelectedItem({ ...data, type });
    } catch (error) {
      console.error('Error fetching detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id, type) => {
    const confirmMsg = type === 'offer' ? t('confirmDeleteOffer') : t('confirmDeleteRequest');
    if (!window.confirm(confirmMsg)) return;
    try {
      const endpoint = type === 'offer' ? 'offers' : 'requests';
      await axios.delete(`${API}/${endpoint}/${id}`);
      toast.success(type === 'offer' ? t('offerDeleted') : t('requestDeleted'));
      setSelectedItem(null);
      fetchMyOffers();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const companyStatus = user?.company?.status || 'pending';

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <AppHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Company Status Alert */}
        {companyStatus === 'pending' && (
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-yellow-50 border border-yellow-200 rounded-xl" data-testid="pending-status-alert">
            <div className="flex items-start gap-2 sm:gap-3">
              <Clock size={20} className="text-yellow-600 flex-shrink-0 mt-1 sm:hidden" />
              <Clock size={24} className="text-yellow-600 flex-shrink-0 mt-1 hidden sm:block" />
              <div>
                <h3 className="font-heading font-bold text-yellow-900 mb-1 sm:mb-2 text-sm sm:text-base">{t('accountPending')}</h3>
                <p className="text-yellow-800 text-xs sm:text-sm">{t('accountPendingDesc')}</p>
              </div>
            </div>
          </div>
        )}

        {companyStatus === 'rejected' && (
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-red-50 border border-red-200 rounded-xl" data-testid="rejected-status-alert">
            <div className="flex items-start gap-2 sm:gap-3">
              <XCircle size={20} className="text-red-600 flex-shrink-0 mt-1 sm:hidden" />
              <XCircle size={24} className="text-red-600 flex-shrink-0 mt-1 hidden sm:block" />
              <div>
                <h3 className="font-heading font-bold text-red-900 mb-1 sm:mb-2 text-sm sm:text-base">{t('accountRejected')}</h3>
                <p className="text-red-800 text-xs sm:text-sm">{t('accountRejectedDesc')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-heading font-bold text-primary mb-1 sm:mb-2">
            {t('welcome')}, {user?.name}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">{user?.company?.name || t('appName')}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-white p-4 sm:p-6 border border-border rounded-xl card-hover" data-testid="stat-total-offers">
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <Package size={24} className="text-accent sm:hidden" />
              <Package size={32} className="text-accent hidden sm:block" />
              <span className="text-2xl sm:text-3xl font-heading font-bold text-primary">{offers.length}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('totalOffers')}</p>
          </div>
          
          <div className="bg-white p-4 sm:p-6 border border-border rounded-xl card-hover" data-testid="stat-total-requests">
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <ShoppingCart size={24} className="text-success sm:hidden" />
              <ShoppingCart size={32} className="text-success hidden sm:block" />
              <span className="text-2xl sm:text-3xl font-heading font-bold text-primary">{requests.length}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('totalRequests')}</p>
          </div>
          
          <div className="bg-white p-4 sm:p-6 border border-border rounded-xl card-hover" data-testid="stat-approved">
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <CheckCircle size={24} className="text-success sm:hidden" />
              <CheckCircle size={32} className="text-success hidden sm:block" />
              <span className="text-2xl sm:text-3xl font-heading font-bold text-primary">
                {offers.filter(o => o.status === 'approved').length + requests.filter(r => r.status === 'approved').length}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('approvedItems')}</p>
          </div>
          
          <div className="bg-white p-4 sm:p-6 border border-border rounded-xl card-hover" data-testid="stat-pending">
            <div className="flex items-center justify-between mb-1 sm:mb-2">
              <Clock size={24} className="text-yellow-600 sm:hidden" />
              <Clock size={32} className="text-yellow-600 hidden sm:block" />
              <span className="text-2xl sm:text-3xl font-heading font-bold text-primary">
                {offers.filter(o => o.status === 'pending').length + requests.filter(r => r.status === 'pending').length}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground">{t('pendingItems')}</p>
          </div>
        </div>

        {/* Action Buttons */}
        {companyStatus === 'approved' && (
          <div className={`grid grid-cols-1 ${(user?.company?.account_type || 'both') === 'both' ? 'sm:grid-cols-2' : ''} gap-4 sm:gap-6 mb-6 sm:mb-8`}>
            {(user?.company?.account_type || 'both') !== 'buyer' && (
            <Link to="/offers/new" data-testid="create-offer-link">
              <div className="bg-gradient-to-br from-accent/10 to-accent/5 p-5 sm:p-8 rounded-xl border border-accent/20 hover:border-accent/40 card-hover cursor-pointer group">
                <Plus size={36} className="mb-3 sm:mb-4 text-accent group-hover:scale-110 transition-transform sm:hidden" />
                <Plus size={48} className="mb-3 sm:mb-4 text-accent group-hover:scale-110 transition-transform hidden sm:block" />
                <h3 className="text-lg sm:text-2xl font-heading font-bold mb-1 sm:mb-2 text-primary">{t('createOffer')}</h3>
                <p className="text-xs sm:text-base text-muted-foreground">{t('createOfferDesc')}</p>
              </div>
            </Link>
            )}
            
            {(user?.company?.account_type || 'both') !== 'seller' && (
            <Link to="/requests/new" data-testid="create-request-link">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 sm:p-8 rounded-xl border border-green-200/50 hover:border-green-300 card-hover cursor-pointer group">
                <Plus size={36} className="mb-3 sm:mb-4 text-success group-hover:scale-110 transition-transform sm:hidden" />
                <Plus size={48} className="mb-3 sm:mb-4 text-success group-hover:scale-110 transition-transform hidden sm:block" />
                <h3 className="text-lg sm:text-2xl font-heading font-bold mb-1 sm:mb-2 text-primary">{t('createRequest')}</h3>
                <p className="text-xs sm:text-base text-muted-foreground">{t('createRequestDesc')}</p>
              </div>
            </Link>
            )}
          </div>
        )}

        {/* Recent Offers */}
        <div className="mb-6 sm:mb-8">
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-primary mb-3 sm:mb-4">{t('myOffers')}</h2>
          {offers.length === 0 ? (
            <div className="bg-white p-8 sm:p-12 border border-border rounded-xl text-center" data-testid="no-offers">
              <Package size={48} className="mx-auto mb-3 sm:mb-4 text-muted-foreground sm:hidden" />
              <Package size={64} className="mx-auto mb-3 sm:mb-4 text-muted-foreground hidden sm:block" />
              <p className="text-muted-foreground">{t('noOffers')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.map((offer) => (
                <div key={offer.id} onClick={() => { setDashImgIndex(0); openDetail(offer.id, 'offer'); }} className="bg-white border border-border rounded-xl card-hover cursor-pointer overflow-hidden" data-testid={`offer-card-${offer.id}`}>
                  {offer.images && offer.images.length > 0 && (
                    <div className="relative">
                      <img src={getImageUrl(offer.images[0])} alt={offer.title} className="w-full h-36 object-cover" />
                      {offer.images.length > 1 && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">{offer.images.length} +</span>
                      )}
                    </div>
                  )}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-heading font-bold text-primary truncate">{offer.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">#{offer.id.slice(-8)}</p>
                      </div>
                      {getStatusBadge(offer.status)}
                    </div>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{offer.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl sm:text-2xl font-heading font-bold text-accent">{formatPrice(offer.price, offer.currency)}</span>
                      <span className="text-sm text-muted-foreground">{t('quantity')}: {offer.quantity}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                      <Eye size={14} />
                      <span>{offer.views || 0} {language === 'ar' ? 'مشاهدة' : 'views'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Requests */}
        <div>
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-primary mb-3 sm:mb-4">{t('myRequests')}</h2>
          {requests.length === 0 ? (
            <div className="bg-white p-8 sm:p-12 border border-border rounded-xl text-center" data-testid="no-requests">
              <ShoppingCart size={48} className="mx-auto mb-3 sm:mb-4 text-muted-foreground sm:hidden" />
              <ShoppingCart size={64} className="mx-auto mb-3 sm:mb-4 text-muted-foreground hidden sm:block" />
              <p className="text-muted-foreground">{t('noRequests')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {requests.map((request) => (
                <div key={request.id} onClick={() => { setDashImgIndex(0); openDetail(request.id, 'request'); }} className="bg-white border border-border rounded-xl card-hover cursor-pointer overflow-hidden" data-testid={`request-card-${request.id}`}>
                  {request.images && request.images.length > 0 && (
                    <div className="relative">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${request.images[0]}`} alt={request.title} className="w-full h-36 object-cover" />
                      {request.images.length > 1 && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-black/60 text-white text-xs rounded-full">{request.images.length} +</span>
                      )}
                    </div>
                  )}
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-heading font-bold text-primary truncate">{request.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1">#{request.id.slice(-8)}</p>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{request.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl sm:text-2xl font-heading font-bold text-green-600">{formatPrice(request.budget, request.currency)}</span>
                      <span className="text-sm text-muted-foreground">{t('quantity')}: {request.quantity}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                      <Eye size={14} />
                      <span>{request.views || 0} {language === 'ar' ? 'مشاهدة' : 'views'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {(selectedItem || detailLoading) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedItem(null)} data-testid="dashboard-detail-modal-overlay">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dashboard-detail-modal">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
              </div>
            ) : selectedItem && (
              <>
                <div className={`p-6 border-b border-border ${selectedItem.type === 'offer' ? 'bg-accent/5' : 'bg-green-50'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-3 ${
                        selectedItem.type === 'offer' ? 'bg-accent/10 text-accent' : 'bg-green-100 text-green-700'
                      }`}>
                        {selectedItem.type === 'offer' ? t('myOffers') : t('myRequests')}
                      </span>
                      <h2 className="text-2xl font-heading font-bold text-primary">{selectedItem.title}</h2>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Hash size={12} /> {selectedItem.id}
                      </p>
                    </div>
                    <button onClick={() => setSelectedItem(null)} data-testid="close-dashboard-detail" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                      <X size={20} className="text-muted-foreground" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Image Gallery */}
                  {selectedItem.images && selectedItem.images.length > 0 && (
                    <div className="rounded-xl overflow-hidden border border-border" data-testid="dashboard-detail-image">
                      <div className="relative">
                        <img 
                          src={getImageUrl(selectedItem.images[dashImgIndex])} 
                          alt={`${selectedItem.title} - ${dashImgIndex + 1}`} 
                          className="w-full h-56 sm:h-72 object-cover transition-opacity duration-200"
                        />
                        {selectedItem.images.length > 1 && (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDashImgIndex(i => i > 0 ? i - 1 : selectedItem.images.length - 1); }}
                              className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'right-2' : 'left-2'} w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors`}
                              data-testid="dash-image-prev"
                            >
                              {language === 'ar' ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDashImgIndex(i => i < selectedItem.images.length - 1 ? i + 1 : 0); }}
                              className={`absolute top-1/2 -translate-y-1/2 ${language === 'ar' ? 'left-2' : 'right-2'} w-9 h-9 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-colors`}
                              data-testid="dash-image-next"
                            >
                              {language === 'ar' ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                            </button>
                            <span className="absolute top-3 right-3 px-2 py-0.5 bg-black/50 text-white text-xs rounded-full">
                              {dashImgIndex + 1} / {selectedItem.images.length}
                            </span>
                            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                              {selectedItem.images.map((_, idx) => (
                                <button key={idx} onClick={(e) => { e.stopPropagation(); setDashImgIndex(idx); }}
                                  className={`w-2 h-2 rounded-full transition-all ${idx === dashImgIndex ? 'bg-white w-5' : 'bg-white/50'}`} />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {selectedItem.images.length > 1 && (
                        <div className="flex gap-1.5 p-2 bg-gray-50 overflow-x-auto">
                          {selectedItem.images.map((img, idx) => (
                            <button key={idx} onClick={(e) => { e.stopPropagation(); setDashImgIndex(idx); }}
                              className={`flex-shrink-0 w-16 h-12 rounded-md overflow-hidden border-2 transition-all ${idx === dashImgIndex ? 'border-accent opacity-100' : 'border-transparent opacity-60 hover:opacity-80'}`}>
                              <img src={getImageUrl(img)} alt={`${idx + 1}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <DollarSign size={20} className={selectedItem.type === 'offer' ? 'text-accent' : 'text-green-600'} />
                      <span className="text-sm font-medium text-muted-foreground">
                        {selectedItem.type === 'offer' ? t('price') : t('budget')}
                      </span>
                    </div>
                    <span className={`text-3xl font-heading font-bold ${selectedItem.type === 'offer' ? 'text-accent' : 'text-green-600'}`}>
                      {selectedItem.type === 'offer' ? formatPrice(selectedItem.price, selectedItem.currency) : formatPrice(selectedItem.budget, selectedItem.currency)}
                    </span>
                  </div>

                  {/* Views */}
                  {selectedItem.views !== undefined && (
                    <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 rounded-xl" data-testid="detail-views">
                      <Eye size={18} className="text-blue-500" />
                      <span className="text-sm font-medium text-blue-700">
                        {selectedItem.views || 0} {language === 'ar' ? 'مشاهدة' : 'views'}
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-xs text-muted-foreground mb-1">{t('quantity')}</p>
                      <p className="text-lg font-bold text-primary">{selectedItem.quantity}</p>
                    </div>
                    {selectedItem.category && (
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Tag size={12} />{t('category')}</p>
                        <p className="text-lg font-bold text-primary">
                          {language === 'ar' ? selectedItem.category.name_ar : selectedItem.category.name_en}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-muted-foreground mb-2">{t('description')}</h3>
                    <p className="text-foreground leading-relaxed whitespace-pre-wrap">{selectedItem.description}</p>
                  </div>

                  {selectedItem.expiration_date && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar size={16} />
                      <span>{t('expirationDate')}: {new Date(selectedItem.expiration_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}</span>
                    </div>
                  )}

                  <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                    <span className="text-sm text-muted-foreground">{t('status')}:</span>
                    {getStatusBadge(selectedItem.status)}
                  </div>

                  {selectedItem.created_at && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar size={12} />
                      {new Date(selectedItem.created_at).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}

                  {/* Delete Button */}
                  <div className="pt-4 border-t border-border">
                    <button
                      onClick={() => handleDelete(selectedItem.id, selectedItem.type)}
                      data-testid="delete-item-btn"
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold transition-all"
                    >
                      <Trash2 size={18} />
                      {t('delete')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
